import { ApiError, api, apiFetch } from "../../api";
import type { Annotation, AnnotationStroke, ApiMeta, Arrangement, Attachment, PlanDetail, SongDetail, SongSummary, UserPreferences } from "../../../shared/types";

interface Envelope<T> {
  success: boolean;
  data: T;
  error: string | null;
  meta: ApiMeta | null;
}

export interface SongListQuery {
  q?: string;
  tag?: string;
  key?: string;
  favorite?: boolean;
  page?: number;
  limit?: number;
}

export interface SongWrite {
  title: string;
  authors: string;
  ccli: string | null;
  bpm: number | null;
  meter: string;
  defaultKey: string;
  tags: string[];
  notes: string;
  arrangementName?: string;
  chordpro?: string;
}

export interface ArrangementWrite {
  name?: string;
  keyName?: string;
  bpm?: number | null;
  meter?: string;
  chordpro?: string;
  isDefault?: boolean;
}

export async function apiWithMeta<T>(path: string, init: RequestInit = {}): Promise<{ data: T; meta: ApiMeta | null }> {
  const response = await apiFetch(path, init);
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("json")) {
    if (!response.ok) throw new ApiError(response.status, "Request failed");
    return { data: undefined as T, meta: null };
  }
  const body = (await response.json()) as Envelope<T>;
  if (response.status === 401 && !path.endsWith("/auth/session")) window.dispatchEvent(new CustomEvent("mb-unauthorized"));
  if (!response.ok || !body.success) throw new ApiError(response.status, body.error ?? "Request failed");
  return { data: body.data, meta: body.meta };
}

export async function listSongs(query: SongListQuery = {}): Promise<{ songs: SongSummary[]; total: number; libraryTotal: number | null }> {
  const params = new URLSearchParams();
  params.set("limit", String(query.limit ?? 50));
  params.set("page", String(query.page ?? 1));
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.tag?.trim()) params.set("tag", query.tag.trim());
  if (query.key) params.set("key", query.key);
  if (query.favorite) params.set("favorite", "1");
  const { data, meta } = await apiWithMeta<SongSummary[]>(`/api/v1/songs?${params.toString()}`);
  return { songs: data, total: meta?.total ?? data.length, libraryTotal: meta?.libraryTotal ?? null };
}

export const getSong = (songId: string) => api<SongDetail>(`/api/v1/songs/${songId}`);
export const createSong = (body: SongWrite) => api<SongDetail>("/api/v1/songs", { method: "POST", body: JSON.stringify(body) });
export const updateSong = (songId: string, body: Partial<SongWrite>) => api<SongDetail>(`/api/v1/songs/${songId}`, { method: "PATCH", body: JSON.stringify(body) });
export const archiveSong = (songId: string) => api<{ archived: boolean }>(`/api/v1/songs/${songId}`, { method: "DELETE" });
export const toggleFavorite = (songId: string) => api<{ favorite: boolean }>(`/api/v1/songs/${songId}/favorite`, { method: "POST" });

export const createArrangement = (songId: string, body: ArrangementWrite) =>
  api<Arrangement>(`/api/v1/songs/${songId}/arrangements`, { method: "POST", body: JSON.stringify(body) });

export const updateArrangement = (songId: string, arrangementId: string, body: ArrangementWrite) =>
  api<Arrangement>(`/api/v1/songs/${songId}/arrangements/${arrangementId}`, { method: "PATCH", body: JSON.stringify(body) });

export async function uploadAttachment(songId: string, file: File, label: string): Promise<Attachment> {
  const body = new FormData();
  body.set("file", file);
  if (label.trim()) body.set("label", label.trim().slice(0, 140));
  const { data } = await apiWithMeta<Attachment>(`/api/v1/songs/${songId}/attachments`, { method: "POST", body });
  return data;
}

export const deleteAttachment = (attachmentId: string) => api<{ deleted: boolean }>(`/api/v1/attachments/${attachmentId}`, { method: "DELETE" });

export const getPlan = (planId: string) => api<PlanDetail>(`/api/v1/plans/${planId}`);

export function annotationQuery(songId: string, planId: string | null, arrangementId: string | null): string {
  const params = new URLSearchParams({ songId });
  if (planId) params.set("planId", planId);
  if (arrangementId) params.set("arrangementId", arrangementId);
  return `/api/v1/annotations?${params.toString()}`;
}

export const listAnnotations = (songId: string, planId: string | null, arrangementId: string | null) =>
  api<Annotation[]>(annotationQuery(songId, planId, arrangementId));

export interface AnnotationSave {
  songId: string;
  arrangementId: string | null;
  planId: string | null;
  pageIndex: number;
  strokes: AnnotationStroke[];
}

export const saveAnnotation = (body: AnnotationSave) =>
  api<{ id: string; updatedAt: string; strokes: AnnotationStroke[] }>("/api/v1/annotations", { method: "PUT", body: JSON.stringify(body) });

export const saveStandFont = (standFont: number) =>
  api<UserPreferences>("/api/v1/settings/preferences", { method: "PATCH", body: JSON.stringify({ standFont }) });

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function typedFile(file: File): File {
  if (file.type) return file;
  const name = file.name.toLowerCase();
  const mime = name.endsWith(".pdf")
    ? "application/pdf"
    : name.endsWith(".mp3")
      ? "audio/mpeg"
      : name.endsWith(".wav")
        ? "audio/wav"
        : name.endsWith(".ogg")
          ? "audio/ogg"
          : name.endsWith(".m4a")
            ? "audio/mp4"
            : name.endsWith(".webm")
              ? "audio/webm"
              : "";
  return mime ? new File([file], file.name, { type: mime }) : file;
}

export const FILE_LIMIT = 12 * 1024 * 1024;

export function messageOf(caught: unknown): string {
  return caught instanceof Error ? caught.message : "Something went wrong";
}
